import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Assuming UserInfo type is correctly defined in '../../types'
import { UserInfo } from '../../types'; // Ensure this path is correct in your project

const CreateDrawPage: React.FC = () => {
    const navigate = useNavigate();

    const [name, setName] = useState<string>('');
    const [deadline, setDeadline] = useState<string>('');
    const [entryCost, setEntryCost] = useState<number>(0);
    const [prizePoolType, setPrizePoolType] = useState<'fixed' | 'percentage'>('fixed');
    const [prizeValue, setPrizeValue] = useState<number>(0);
    const [isVIP, setIsVIP] = useState<boolean>(false); // New state for VIP draw
    const [numberRangeMax, setNumberRangeMax] = useState<number>(99); // New state for VIP number range max
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    // Retrieve user info from local storage
    const userInfoString = localStorage.getItem('userInfo');
    const user: UserInfo | null = userInfoString ? JSON.parse(userInfoString) : null;

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(''); // Clear previous errors
        setSuccess(''); // Clear previous success messages

        // Basic validation: Check if user is authorized
        if (!user || !user.token || !user.isAdmin) {
            setError('Unauthorized: You must be logged in as an administrator.');
            return;
        }

        // Basic form field validation
        if (!name || !deadline || entryCost <= 0 || prizeValue <= 0) {
            setError('Please fill in all required fields with valid values.');
            return;
        }

        // Specific validation for percentage prize type
        if (prizePoolType === 'percentage' && (prizeValue < 1 || prizeValue > 100)) {
            setError('Percentage prize must be between 1 and 100.');
            return;
        }

        // VIP Draw specific validation
        if (isVIP) {
            if (numberRangeMax === undefined || numberRangeMax < 0 || numberRangeMax > 99) {
                setError('For VIP draws, Number Pool Size must be between 0 and 99.');
                return;
            }
        }

        setLoading(true); // Set loading state
        try {
            // API call to create a new draw
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/draws`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`, // Include authorization token
                },
                body: JSON.stringify({
                    name,
                    deadline,
                    entryCost,
                    prizePoolType,
                    prizeValue,
                    isVIP, // Include new field
                    numberRangeMax: isVIP ? numberRangeMax : 99, // Conditionally send numberRangeMax
                }),
            });

            const data = await res.json(); // Parse response data

            // Check if the response was successful
            if (!res.ok) {
                throw new Error(data.message || 'Failed to create draw.');
            }

            setSuccess(`Draw "${data.draw.name}" created successfully!`); // Use data.draw.name for confirmation
            setLoading(false); // End loading state

            // Clear form fields after successful submission
            setName('');
            setDeadline('');
            setEntryCost(0);
            setPrizePoolType('fixed');
            setPrizeValue(0);
            setIsVIP(false); // Reset VIP state
            setNumberRangeMax(99); // Reset number range

            // Redirect to manage draws page after a short delay
            setTimeout(() => {
                navigate('/admin/draws/manage');
            }, 2000);

        } catch (err: any) {
            // Handle any errors during the API call
            setError((err instanceof Error ? err.message : String(err)) || 'Error creating draw.');
            setLoading(false); // End loading state on error
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 sm:p-6 lg:p-8 font-inter">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 lg:p-10 w-full max-w-md border border-gray-100 transform transition-all duration-300 hover:scale-[1.01]">
                <h1 className="text-3xl font-extrabold text-center mb-7 text-gray-900 drop-shadow-sm">
                    Create New Draw
                </h1>

                {/* Success Message */}
                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg relative mb-5 shadow-md animate-fade-in-down">
                        <strong className="font-bold">Success!</strong>
                        <span className="block sm:inline ml-2">{success}</span>
                    </div>
                )}
                {/* Error Message */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-5 shadow-md animate-fade-in-down">
                        <strong className="font-bold">Error!</strong>
                        <span className="block sm:inline ml-2">{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Draw Name Input */}
                    <div>
                        <label htmlFor="name" className="block text-gray-700 text-sm font-semibold mb-2">
                            Draw Name:
                        </label>
                        <input
                            type="text"
                            id="name"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            disabled={loading}
                        />
                    </div>

                    {/* Deadline Input */}
                    <div>
                        <label htmlFor="deadline" className="block text-gray-700 text-sm font-semibold mb-2">
                            Deadline:
                        </label>
                        <input
                            type="datetime-local"
                            id="deadline"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            value={deadline}
                            onChange={(e) => setDeadline(e.target.value)}
                            required
                            disabled={loading}
                        />
                    </div>

                    {/* Entry Cost Input */}
                    <div>
                        <label htmlFor="entryCost" className="block text-gray-700 text-sm font-semibold mb-2">
                            Entry Cost (Rs.):
                        </label>
                        <input
                            type="number"
                            id="entryCost"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            value={entryCost}
                            onChange={(e) => setEntryCost(parseFloat(e.target.value) || 0)}
                            min="0"
                            required
                            disabled={loading}
                        />
                    </div>

                    {/* Prize Pool Type Select */}
                    <div>
                        <label htmlFor="prizePoolType" className="block text-gray-700 text-sm font-semibold mb-2">
                            Prize Pool Type:
                        </label>
                        <select
                            id="prizePoolType"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            value={prizePoolType}
                            onChange={(e) => setPrizePoolType(e.target.value as 'fixed' | 'percentage')}
                            disabled={loading}
                        >
                            <option value="fixed">Fixed Amount</option>
                            <option value="percentage">Percentage of Total Pool</option>
                        </select>
                    </div>

                    {/* Prize Value Input */}
                    <div>
                        <label htmlFor="prizeValue" className="block text-gray-700 text-sm font-semibold mb-2">
                            Prize Value ({prizePoolType === 'percentage' ? '%' : 'Rs.'}):
                        </label>
                        <input
                            type="number"
                            id="prizeValue"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            value={prizeValue}
                            onChange={(e) => setPrizeValue(parseFloat(e.target.value) || 0)}
                            min="0"
                            max={prizePoolType === 'percentage' ? 100 : undefined}
                            required
                            disabled={loading}
                        />
                    </div>

                    {/* VIP Draw Checkbox */}
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="isVIP"
                            className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            checked={isVIP}
                            onChange={(e) => setIsVIP(e.target.checked)}
                            disabled={loading}
                        />
                        <label htmlFor="isVIP" className="text-gray-700 text-sm font-semibold">
                            This is a VIP Draw (Limited Numbers)
                        </label>
                    </div>

                    {/* Number Pool Size for VIP Draws (Conditional) */}
                    {isVIP && (
                        <div>
                            <label htmlFor="numberRangeMax" className="block text-gray-700 text-sm font-semibold mb-2">
                                Number Pool Size (0 to X):
                            </label>
                            <input
                                type="number"
                                id="numberRangeMax"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                                value={numberRangeMax}
                                onChange={(e) => setNumberRangeMax(parseInt(e.target.value) || 0)}
                                min="0"
                                max="99"
                                required={isVIP}
                                disabled={loading}
                            />
                            <p className="text-xs text-gray-500 mt-1">Enter the highest number for the pool (e.g., enter '9' for numbers 0-9).</p>
                        </div>
                    )}


                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
                        <button
                            type="submit"
                            className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white font-extrabold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? 'Creating...' : 'Create Draw'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/dashboard')}
                            className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateDrawPage;
